import { Dispatch, SetStateAction, useCallback } from 'react';
import { toast } from 'react-toastify';
import { TFunction } from 'i18next';
import { DebouncedState } from 'use-debounce/dist/useDebouncedCallback';
import { mapAttachmentItemDataFromFile } from '90.quickConnect.Models/mappings';
import { AttachmentItemData, BaseAttachmentDesc } from '90.quickConnect.Models/models';
import attachmentDb from '40.quickConnect.DataAccess/indexedDb/dbs/attachmentDb';
import { useStore } from '30.quickConnect.Stores';

// Liste des signatures binaires connues pour les fichiers HEIC/HEIF
// Ces signatures sont présentes dans les 32 premiers octets des fichiers valides
const knownHeicSignatures = [
  'ftypheic', // Format standard HEIC
  'ftypheix', // HEIF étendu
  'ftyphevc', // HEVC encodé
  'ftyphevx', // Variante HEVC
  'ftypmif1', // Format générique HEIF
  'ftypmsf1', // Format de séquences HEIF
  'ftypheim', // Variante Apple
];

// Signatures binaires pour les formats image classiques
const knownImageSignatures: {
  mime: string;
  signature: number[];
  offset?: number;
  extraCheck?: (bytes: Uint8Array) => boolean;
}[] = [
  {
    mime: 'image/png',
    signature: [0x89, 0x50, 0x4e, 0x47], // PNG
  },
  {
    mime: 'image/jpeg',
    signature: [0xff, 0xd8, 0xff], // JPEG
  },
  {
    mime: 'image/gif',
    signature: [0x47, 0x49, 0x46, 0x38], // GIF base, vérifier ensuite 39 ou 37 et 61
    extraCheck: (bytes) => (bytes[4] === 0x39 || bytes[4] === 0x37) && bytes[5] === 0x61,
  },
  {
    mime: 'image/webp',
    signature: [0x52, 0x49, 0x46, 0x46], // RIFF
    offset: 8,
    extraCheck: (bytes) => bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50, // "WEBP"
  },
];

// Fonction pour détecter si un fichier est de type valide via son contenu binaire
// Pour voir si c'est déguisé (HEIC.jpg)
const detectRealMimeType = async (file: File): Promise<string | null> => {
  const buffer = await file.slice(0, 16).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  // HEIC/HEIF signatures
  const headerStr = String.fromCharCode(...bytes);
  if (knownHeicSignatures.some((sig) => headerStr.includes(sig))) {
    return 'image/heic';
  }

  // On check les signatures binaires classiques
  for (const { mime, signature, offset = 0, extraCheck } of knownImageSignatures) {
    const match = signature.every((byte, i) => bytes[offset + i] === byte);
    if (match && (!extraCheck || extraCheck(bytes))) {
      return mime;
    }
  }

  return null;
};

// Si le type MIME du fichier est incorrect ou vide, on le corrige à partir du contenu réel
const normalizeFileForResize = async (file: File): Promise<File> => {
  const realMime = await detectRealMimeType(file);

  // Si on détecte un vrai type différent, on crée un nouveau fichier avec le bon type
  if (realMime && realMime !== file.type) {
    const buffer = await file.arrayBuffer();
    return new File([buffer], file.name, {
      type: realMime,
      lastModified: file.lastModified,
    });
  }

  // Sinon, on retourne le fichier tel quel
  return file;
};

// Noms "génériques" produits par les navigateurs lors d'une prise de photo
// (Safari iOS/iPadOS donne toujours "image.jpg"). On les renomme
// systématiquement pour que l'utilisateur voie image_xxx.jpg
const GENERIC_CAMERA_NAMES = /^image\.(jpe?g|png|heic|heif|webp)$/i;

const makeUniqueFileName = (originalName: string, existingNames: Set<string>): string => {
  const isGeneric = GENERIC_CAMERA_NAMES.test(originalName);

  // Si le nom est unique ET n'est pas un nom générique d'appareil photo,
  // on garde le nom d'origine.
  if (!isGeneric && !existingNames.has(originalName)) return originalName;

  const dotIndex = originalName.lastIndexOf('.');
  const base = dotIndex > 0 ? originalName.slice(0, dotIndex) : originalName;
  const ext = dotIndex > 0 ? originalName.slice(dotIndex) : '';

  return `${base}_${Date.now()}${ext}`;
};

const useAttachments = (
  onlyImage: boolean,
  localValue: AttachmentItemData[],
  setLocalValue: Dispatch<SetStateAction<AttachmentItemData[]>>,
  t: TFunction,
  attachDesc: BaseAttachmentDesc,
  debouncedUpdateGlobalState: DebouncedState<() => void>,
  isTask?: boolean,
) => {
  const getFileHTMLElement = useCallback((parentId: string): HTMLInputElement | undefined => {
    const fileHMTLElement: HTMLInputElement | null = document.querySelector<HTMLInputElement>(
      `[data-cy="${parentId}-input"]`,
    );
    if (fileHMTLElement === null) return;
    return fileHMTLElement;
  }, []);

  const {
    DeclarationStore: { declarationId },
  } = useStore();

  const verifyAndAddFile = useCallback(
    async (files: File[]) => {
      //Récupération de la taille des PJ déjà ajoutées
      let nbAttach = localValue.length;
      let messageDuplicateError = '';
      let messageErrors = '';
      let nbDuplicate = 0;

      async function getAddFileToAttachmentDb(file: File) {
        //Pour chaque fichier à ajouter, vérification si déjà présent

        // Détection d'un vrai doublon : même nom + même taille + même date de modif.
        // (Sur Safari iPad, toutes les photos prises depuis l'appareil photo
        // s'appellent "image.jpg" → on ne peut pas se baser uniquement sur le nom.)
        const alreadyAdded = localValue.some(
          (addedFile) =>
            addedFile.fileName === file.name &&
            addedFile.size === file.size &&
            (addedFile.file as File | undefined)?.lastModified === file.lastModified,
        );
        if (!alreadyAdded) {
          // Renommage systématique des noms génériques type "image.jpg",
          // et résolution de collision pour les autres noms.
          const existingNames = new Set(localValue.map((a) => a.fileName));
          const uniqueName = makeUniqueFileName(file.name, existingNames);
          if (uniqueName !== file.name) {
            file = new File([file], uniqueName, {
              type: file.type,
              lastModified: file.lastModified,
            });
          }
          //Vérification du nombre max d'attachment
          if (attachDesc.maxAttachment >= nbAttach + 1 || (isTask && nbAttach + 1 < 2)) {
            //Vérification de la taille
            if (attachDesc.maximumSize * 1024 ** 2 >= file.size || isTask) {
              nbAttach++;
              const attachItem = await mapAttachmentItemDataFromFile(file, file.name);

              // Persister le fichier avant de l'afficher : une validation immédiate doit pouvoir
              // retrouver cette ligne dans IndexedDB et passer son transferState de 0 à 1.
              await attachmentDb.addAttachmentItem(attachItem, file, declarationId, false);

              setLocalValue((prev) => {
                const newArray = [...prev, attachItem];
                return newArray;
              });

              // Laisser React appliquer le nouveau localValue avant de propager la liste complète
              // dans l'état global de la déclaration ; le debounce évite les mises à jour rapprochées.
              setTimeout(() => {
                debouncedUpdateGlobalState();
              }, 100);
            } else {
              //Taille maximale autorisée atteinte
              messageErrors = `${t('attachment_fields_errors_has_max_size')} (${attachDesc.maximumSize} Mo)`;
              return;
            }
          } else {
            //Nombre max attachment atteint
            messageErrors = `${t('attachment_fields_errors_has_max_count')} (${attachDesc.maxAttachment})`;
            return;
          }
        } else {
          //Fichier déjà sélectionné
          messageDuplicateError += `${messageDuplicateError ? '\n-' : '-'} ${file.name}`;
          nbDuplicate++;
        }
      }

      for (const file of files) {
        // Vérifier et corriger le fichier s'il s'agit d'un HEIC avec une mauvaise extension ou type
        const correctedFile = await normalizeFileForResize(file);
        const toAdd = !onlyImage || correctedFile.type.split('/')[0] === 'image';
        if (toAdd) {
          await getAddFileToAttachmentDb(correctedFile);
        } else {
          messageErrors = `${t('attachment_fields_errors_onlyAdd_pictureFiles')}`;
        }
      }

      let message = '';
      if (nbDuplicate > 0 && messageDuplicateError) {
        message = `${
          nbDuplicate > 1 ? t('formlib_already_add_attachment') : t('formlib_already_add_attachment')
        }\n${messageDuplicateError}`;
      }
      message += `${message ? '\n' : ''}${messageErrors}`;
      if (message) {
        toast.warn(message);
      }
    },
    [
      declarationId,
      attachDesc.maxAttachment,
      attachDesc.maximumSize,
      debouncedUpdateGlobalState,
      isTask,
      localValue,
      onlyImage,
      setLocalValue,
      t,
    ],
  );

  const handleFileDrop = useCallback(
    async (item: any, parentId: string) => {
      const fileElement = getFileHTMLElement(parentId);
      const valueFile = fileElement?.value ?? '';
      if (item) {
        const { files } = item;
        await verifyAndAddFile(files); // attendre pour que les messages soient prêts
      }
    },
    [verifyAndAddFile, getFileHTMLElement],
  );

  const handleFileDelete = useCallback(
    (item: any) => {
      setLocalValue((prev) => {
        const newArray = [...prev.filter((file) => file.id !== item.id)];
        return newArray;
      });
      setTimeout(async () => {
        await attachmentDb.deleteFromId(item);
        debouncedUpdateGlobalState();
      }, 100);
    },
    [debouncedUpdateGlobalState, setLocalValue],
  );

  const handleFilePreview = useCallback((attachment: AttachmentItemData) => {
    let link = '';

    // If it's a word, powerpoint, or excel, open the file using the office viewer
    if (/.?(word|powerpoint|excel)/.test(attachment.type)) {
      link = 'https://view.officeapps.live.com/op/view.aspx?src=';
    }

    // Create a local url for the file if it's not uploaded yet, use the distant link instead
    if (attachment.distantUri) {
      link += encodeURI(attachment.distantUri);
    } else if ((attachment.file as File)?.type) {
      link += encodeURI(URL.createObjectURL(attachment.file as File));
    }

    window.open(link, '_blank');
    return false;
  }, []);

  return { handleFileDrop, handleFileDelete, handleFilePreview };
};

export default useAttachments;
